'use client'

import { useEffect, useState } from 'react'
import { SidebarTrigger, useSidebar } from 'fumadocs-ui/components/sidebar/base'
import { useSearchContext } from 'fumadocs-ui/contexts/search'
import { Menu, Search, X } from 'lucide-react'
import type { DocsLocale } from '@/lib/docs-i18n'
import { DocsBrandRow } from '@/components/docs/DocsBrandRow'

/**
 * Mobile docs top bar — matches desktop sidebar brand (logo + "/ Docs") instead
 * of Fumadocs' plain text nav.title. Sidebar brand row is hidden on mobile so
 * opening the menu does not show a double header.
 */
export function DocsMobileSubNav({ locale }: { locale: DocsLocale }) {
  const { open } = useSidebar()
  const { enabled, setOpenSearch } = useSearchContext()
  const [transparent, setTransparent] = useState(false)

  useEffect(() => {
    const listener = () => {
      setTransparent(window.scrollY < 10)
    }
    listener()
    window.addEventListener('scroll', listener)
    return () => window.removeEventListener('scroll', listener)
  }, [])

  const iconButtonClass =
    'docs-subnav-icon-btn inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md transition-colors'

  return (
    <nav
      aria-label="Docs"
      id="nd-subnav"
      className={`[grid-area:header] sticky top-0 z-40 bk-only-md-down flex flex-col border-b transition-colors bk-header-safe ${
        transparent
          ? 'border-transparent'
          : 'border-fd-foreground/10 bg-fd-background/60 backdrop-blur-md'
      }`}
    >
      <div className="flex h-14 flex-row items-center gap-2 px-4">
        <DocsBrandRow locale={locale} className="min-w-0 flex-1" />
        <div className="flex shrink-0 flex-row items-center gap-0.5">
          {enabled ? (
            <button
              type="button"
              className={iconButtonClass}
              data-search=""
              aria-label="Open Search"
              onClick={() => setOpenSearch(true)}
            >
              <Search className="size-4" aria-hidden />
            </button>
          ) : null}
          <SidebarTrigger
            className={`${iconButtonClass} -me-1`}
            aria-label="Toggle Sidebar"
            data-open={open}
          >
            {open ? <X className="size-4" aria-hidden /> : <Menu className="size-4" aria-hidden />}
          </SidebarTrigger>
        </div>
      </div>
    </nav>
  )
}
